/**
 * @zendir/ui - Clipboard Utilities
 *
 * Copy-to-clipboard hook and button component. Used universally in operator
 * dashboards for sharing configuration URLs, copying telemetry values,
 * exporting command IDs.
 *
 * @example
 * ```tsx
 * // Hook
 * const { copy, copied } = useCopyToClipboard();
 * <Button onClick={() => copy(configUrl)}>
 *   {copied ? 'Copied!' : 'Copy URL'}
 * </Button>
 *
 * // Component
 * <CopyButton value={telemetryValue} label="Copy Value" />
 * <CopyButton value={shareUrl} size="small" />
 * ```
 */

import React, { memo, useState, useCallback, useRef, useEffect } from "react";
import { useTheme } from "../../theme";

// ─── Hook ────────────────────────────────────────────────────────────────────

export interface UseCopyToClipboardReturn {
  /** Copy text to clipboard */
  copy: (text: string) => Promise<boolean>;
  /** True for brief period after successful copy */
  copied: boolean;
  /** Error if copy failed */
  error: string | null;
}

/**
 * Hook for clipboard operations.
 *
 * @param resetDelay - Time in ms before `copied` resets to false (default 2000)
 *
 * @example
 * ```tsx
 * const { copy, copied } = useCopyToClipboard();
 * ```
 */
export function useCopyToClipboard(
  resetDelay = 2000,
): UseCopyToClipboardReturn {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const copy = useCallback(
    async (text: string): Promise<boolean> => {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          // Fallback for older browsers / non-HTTPS
          const textarea = document.createElement("textarea");
          textarea.value = text;
          textarea.style.cssText =
            "position:fixed;left:-9999px;top:-9999px;opacity:0";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }

        setCopied(true);
        setError(null);
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setCopied(false), resetDelay);
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Copy failed";
        setError(message);
        setCopied(false);
        return false;
      }
    },
    [resetDelay],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);

  return { copy, copied, error };
}

// ─── Component ───────────────────────────────────────────────────────────────

export interface CopyButtonProps {
  /** Text to copy */
  value: string;
  /** Button label (default: shows icon only) */
  label?: string;
  /** Label shown briefly after copy (default: "Copied!") */
  copiedLabel?: string;
  /** Size */
  size?: "small" | "medium" | "large";
  /** Tooltip text */
  tooltip?: string;
  /** Disabled */
  disabled?: boolean;
  /** Custom style */
  style?: React.CSSProperties;
}

export const CopyButton = memo(function CopyButton({
  value,
  label,
  copiedLabel = "Copied!",
  size = "medium",
  tooltip = "Copy to clipboard",
  disabled = false,
  style,
}: CopyButtonProps): React.ReactElement {
  const { tokens } = useTheme();
  const { copy, copied } = useCopyToClipboard();
  const [isHovered, setIsHovered] = useState(false);

  const sizeConfig = {
    small: {
      height: 28,
      padding: "4px 8px",
      fontSize: "0.75rem",
      iconSize: 14,
    },
    medium: {
      height: 36,
      padding: "6px 12px",
      fontSize: "0.875rem",
      iconSize: 16,
    },
    large: { height: 44, padding: "8px 16px", fontSize: "1rem", iconSize: 18 },
  };
  const config = sizeConfig[size];

  // Copy icon (two overlapping rectangles)
  const copyIcon = (
    <svg
      width={config.iconSize}
      height={config.iconSize}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );

  // Checkmark icon
  const checkIcon = (
    <svg
      width={config.iconSize}
      height={config.iconSize}
      viewBox="0 0 24 24"
      fill="none"
      stroke={tokens.colors.status.normal}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );

  return (
    <button
      type="button"
      onClick={() => !disabled && copy(value)}
      disabled={disabled}
      title={copied ? copiedLabel : tooltip}
      aria-label={!label ? (copied ? copiedLabel : tooltip) : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        height: config.height,
        padding: label
          ? config.padding
          : `0 ${Math.round(parseInt(config.padding) * 0.6)}px`,
        fontSize: config.fontSize,
        fontWeight: 500,
        fontFamily: tokens.typography.fontFamily.primary,
        color: copied
          ? tokens.colors.status.normal
          : tokens.colors.text.secondary,
        backgroundColor:
          isHovered && !disabled
            ? `${tokens.colors.accent.primary}12`
            : "transparent",
        border: `1px solid ${copied ? `${tokens.colors.status.normal}40` : tokens.colors.border.muted}`,
        borderRadius: tokens.borderRadius.md,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: tokens.animation.fast,
        opacity: disabled ? 0.5 : 1,
        outline: "none",
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {copied ? checkIcon : copyIcon}
      {label && <span>{copied ? copiedLabel : label}</span>}
    </button>
  );
});

export default CopyButton;
